import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImage from '../components/ResizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { DOMParser as PMMDOMParser } from '@tiptap/pm/model';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../utils/api';
import { reuploadExternalImages } from '../utils/pasteImages';
import dayjs from 'dayjs';
import {
  ArrowLeft, Trash2, WifiOff, Plus, GripVertical, X,
  ChevronDown, Eye, Edit2, Layers,
} from 'lucide-react';
import EditorToolbar from '../components/EditorToolbar';
import MarkdownPaste from '../extensions/MarkdownPaste';
import { markdownToHtml } from '../utils/markdownUtils';
import FileAttachments from '../components/FileAttachments';
import { getContrastColor } from '../utils/color';
import { useTheme } from '../contexts/ThemeContext';
import SectionEditor from '../components/SectionEditor';

const COLORS = ['#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9','#f472b6'];
function getUserColor(id) { return COLORS[(id || 0) % COLORS.length]; }

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

const cursorBuilder = (user) => {
  const cursor = document.createElement('span');
  cursor.className = 'yjs-cursor';
  cursor.style.borderColor = user.color;
  const label = document.createElement('span');
  label.className = 'yjs-cursor-label';
  label.style.backgroundColor = user.color;
  label.textContent = user.name;
  cursor.appendChild(label);
  return cursor;
};

// ── 아코디언 섹션 (보기 모드) ──────────────────────────────────────
function AccordionSection({ section, isOpen, onToggle, theme }) {
  return (
    <div className="border border-gray-800 rounded-xl mb-2 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-900/50 hover:bg-gray-900/80 transition-colors text-left gap-3"
      >
        <span className={`text-sm font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'}`}>
          {section.title || '(제목 없음)'}
        </span>
        <ChevronDown
          size={14}
          className={`text-gray-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          {section.content ? (
            <div
              className={`px-6 py-4 prose max-w-none ${theme === 'light' ? '' : 'prose-invert'}`}
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          ) : (
            <p className="px-6 py-4 text-xs text-gray-600">내용 없음</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 드래그 가능한 섹션 카드 (편집 모드) ──────────────────────────
function SortableSection({ section, onTitleChange, onDelete, ydoc, provider, onContentUpdate, onFocus, logId, onAttachRefresh }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-xl mb-3 overflow-hidden transition-opacity ${isDragging ? 'opacity-40' : 'opacity-100'} border-gray-800 bg-gray-950`}
    >
      {/* 섹션 헤더 */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-900/80 border-b border-gray-800">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 text-gray-600 hover:text-gray-400 flex-shrink-0 touch-none"
        >
          <GripVertical size={14} />
        </button>
        <input
          value={section.title}
          onChange={e => onTitleChange(section.id, e.target.value)}
          className="flex-1 bg-transparent text-gray-100 text-sm font-semibold focus:outline-none placeholder-gray-600 min-w-0"
          placeholder="섹션 제목"
        />
        <button
          onClick={() => onDelete(section.id)}
          className="p-0.5 text-gray-600 hover:text-red-400 flex-shrink-0"
        >
          <X size={14} />
        </button>
      </div>
      {/* 섹션 에디터 */}
      <SectionEditor
        ydoc={ydoc}
        provider={provider}
        sectionId={section.id}
        onUpdate={html => onContentUpdate(section.id, html)}
        onFocus={onFocus}
        logId={logId}
        onAttachRefresh={() => {}}
      />
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────
export default function ResearchLogEditorPage() {
  const { theme } = useTheme();
  const avatarColor = (color) => theme === 'light' ? getContrastColor(color) : 'white';
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isAdmin = user.role === 'superadmin' || user.role === 'admin';

  const [log, setLog] = useState(null);
  const [categories, setCategories] = useState([]);
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [attachKey, setAttachKey] = useState(0);
  const fileInputRef = useRef(null);
  const mdFileInputRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const titleSaveTimer = useRef(null);
  const saveStatusTimer = useRef(null);
  const logIdRef = useRef(null);

  // 보기/편집 모드
  const [viewMode, setViewMode] = useState(true);

  // 섹션 상태 (null = 레거시 단일에디터, [] or [...] = 섹션 모드)
  const [sections, setSections] = useState(undefined); // undefined = 로딩 중
  const sectionsRef = useRef([]);
  const sectionContentsRef = useRef({});
  const saveSectionsTimer = useRef(null);
  const [openSections, setOpenSections] = useState({});

  // 편집 모드 공유 툴바를 위한 activeEditor
  const [activeEditor, setActiveEditor] = useState(null);

  // 레거시 에디터 ref (섹션으로 전환 시 HTML 가져오기용)
  const legacyEditorRef = useRef(null);

  // Yjs 문서 + Provider
  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const token = localStorage.getItem('mfg_token');
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const isLocal = ['localhost', '127.0.0.1', '100.116.73.97'].includes(window.location.hostname);
    const wsHost = isLocal ? `${window.location.hostname}:3800` : window.location.host;
    const prov = new WebsocketProvider(`${wsProtocol}//${wsHost}/yjs`, `research-${id}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
  }, [id]);

  const ytitle = useMemo(() => ydoc.getText('title'), [ydoc]);

  const loadLog = useCallback(() =>
    api.get(`/research-logs/${id}`).then(r => {
      setLog(r.data);
      logIdRef.current = r.data.id;

      if (r.data.sections && r.data.sections.length > 0) {
        // 섹션 모드
        setSections(r.data.sections);
        const contents = {};
        r.data.sections.forEach(s => { contents[s.id] = s.content || ''; });
        sectionContentsRef.current = contents;
        sectionsRef.current = r.data.sections;
      } else if (!r.data.content) {
        // 신규 일지 (내용 없음) → 섹션 1개 자동 생성
        const initSection = { id: `sec-${Date.now()}`, title: '섹션 1', sortOrder: 0, content: '' };
        setSections([initSection]);
        sectionContentsRef.current = { [initSection.id]: '' };
        sectionsRef.current = [initSection];
        api.put(`/research-logs/${id}`, { sections: [initSection] }).catch(() => {});
        setViewMode(false); // 신규는 편집 모드로 시작
      } else {
        // 레거시 로그 (기존 단일 에디터 내용 있음)
        setSections(null);
      }
    }), [id]);

  const loadCategories = useCallback(() =>
    api.get('/research-logs/categories').then(r => setCategories(r.data)), []);

  useEffect(() => { loadLog(); loadCategories(); }, [loadLog, loadCategories]);

  // Provider 이벤트
  useEffect(() => {
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    if (provider.wsconnected) setConnected(true);

    provider.awareness.setLocalStateField('user', {
      name: user.name || user.username,
      color: getUserColor(user.id),
    });

    const awarenessHandler = () => {
      const seen = new Set();
      const users = [];
      provider.awareness.getStates().forEach(state => {
        if (state.user && !seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ ...state.user, isMe: false });
        }
      });
      const myName = user.name || user.username;
      if (!seen.has(myName)) {
        users.unshift({ name: myName, color: getUserColor(user.id), isMe: true });
      } else {
        const idx = users.findIndex(u => u.name === myName);
        if (idx >= 0) { users[idx].isMe = true; if (idx > 0) users.unshift(users.splice(idx, 1)[0]); }
      }
      setPeers(users);
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();

    return () => { provider.destroy(); ydoc.destroy(); };
  }, [provider, ydoc]);

  // 제목 Yjs 동기화
  const [title, setTitle] = useState('');
  useEffect(() => {
    const update = () => {
      const val = ytitle.toString();
      setTitle(val);
      if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current);
      titleSaveTimer.current = setTimeout(async () => {
        if (!val) return;
        setSaveStatus('saving');
        try {
          await api.put(`/research-logs/${id}`, { title: val });
          setSaveStatus('saved');
          clearTimeout(saveStatusTimer.current);
          saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
        } catch { setSaveStatus('error'); }
      }, 1000);
    };
    ytitle.observe(update);
    const onSync = (isSynced) => { if (isSynced) setTitle(ytitle.toString()); };
    provider.on('synced', onSync);
    if (provider.synced) setTitle(ytitle.toString());
    return () => {
      ytitle.unobserve(update);
      provider.off('synced', onSync);
      if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current);
    };
  }, [ytitle, provider, id]);

  const handleTitleChange = (e) => {
    const val = e.target.value;
    ydoc.transact(() => {
      ytitle.delete(0, ytitle.length);
      if (val) ytitle.insert(0, val);
    });
  };

  // ── 레거시 단일 에디터 (sections === null) ─────────────────────
  const LegacyYjsExtension = useMemo(() => Extension.create({
    name: 'yjs',
    addProseMirrorPlugins() {
      const fragment = ydoc.getXmlFragment('content');
      return [ySyncPlugin(fragment), yCursorPlugin(provider.awareness, { cursorBuilder }), yUndoPlugin()];
    },
  }), [ydoc, provider]);

  const legacyEditor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      LegacyYjsExtension,
      ResizableImage, TextStyle, Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
      MarkdownPaste,
      Table.configure({ resizable: false }),
      TableRow, TableCell, TableHeader,
    ],
    autofocus: false,
    onUpdate: ({ editor }) => {
      setSaveStatus('saving');
      clearTimeout(legacyContentTimer.current);
      legacyContentTimer.current = setTimeout(async () => {
        try {
          await api.put(`/research-logs/${id}`, { content: editor.getHTML() });
          setSaveStatus('saved');
          clearTimeout(saveStatusTimer.current);
          saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
        } catch { setSaveStatus('error'); }
      }, 2000);
    },
    onFocus: ({ editor }) => setActiveEditor(editor),
    editorProps: {
      attributes: { class: 'prose prose-invert max-w-none focus:outline-none min-h-[400px] px-6 py-4' },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        const nonImageFiles = Array.from(files).filter(f => !f.type.startsWith('image/'));
        if (nonImageFiles.length > 0) {
          event.preventDefault();
          const logId = logIdRef.current;
          if (logId) {
            (async () => {
              for (const f of nonImageFiles) {
                const fd = new FormData();
                fd.append('file', f);
                fd.append('entityType', 'research');
                fd.append('entityId', String(logId));
                await api.post('/files', fd).catch(() => {});
              }
              setAttachKey(k => k + 1);
            })();
          }
          if (imageFiles.length === 0) return true;
        }
        event.preventDefault();
        imageFiles.forEach(async (f) => {
          const url = await uploadImage(f);
          view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
        });
        return true;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (items) {
          for (const item of items) {
            if (item.type.startsWith('image/')) {
              event.preventDefault();
              const file = item.getAsFile();
              if (!file) continue;
              (async () => {
                const url = await uploadImage(file);
                view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
              })();
              return true;
            }
          }
        }
        const html = event.clipboardData?.getData('text/html');
        if (html && /<img[^>]+src=["']https?:\/\//i.test(html)) {
          event.preventDefault();
          (async () => {
            const processed = await reuploadExternalImages(html);
            legacyEditorRef.current?.commands.insertContent(processed);
          })();
          return true;
        }
        return false;
      },
    },
  }, [LegacyYjsExtension]);

  const legacyContentTimer = useRef(null);
  useEffect(() => { legacyEditorRef.current = legacyEditor; }, [legacyEditor]);
  useEffect(() => {
    if (legacyEditor && sections === null) setActiveEditor(legacyEditor);
  }, [legacyEditor, sections]);

  // 이미지/마크다운 업로드
  const handleImageButton = () => fileInputRef.current?.click();
  const handleMdUpload = () => mdFileInputRef.current?.click();
  const handleMdFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeEditor) return;
    const text = await file.text();
    const html = markdownToHtml(text);
    const view = activeEditor.view;
    const parser = PMMDOMParser.fromSchema(view.state.schema);
    const dom = document.createElement('div');
    dom.innerHTML = html;
    const slice = parser.parseSlice(dom, { preserveWhitespace: false });
    const tr = view.state.tr.replaceSelection(slice);
    view.dispatch(tr.scrollIntoView());
    e.target.value = '';
  };
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !activeEditor) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      activeEditor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  // ── 섹션 관리 ─────────────────────────────────────────────────
  const scheduleSectionSave = useCallback(() => {
    setSaveStatus('saving');
    clearTimeout(saveSectionsTimer.current);
    saveSectionsTimer.current = setTimeout(async () => {
      try {
        const toSave = sectionsRef.current.map(s => ({
          id: s.id, title: s.title, sortOrder: s.sortOrder,
          content: sectionContentsRef.current[s.id] || '',
        }));
        await api.put(`/research-logs/${id}`, { sections: toSave });
        setSaveStatus('saved');
        clearTimeout(saveStatusTimer.current);
        saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
      } catch { setSaveStatus('error'); }
    }, 2000);
  }, [id]);

  const handleSectionTitleChange = useCallback((sectionId, newTitle) => {
    setSections(prev => {
      const next = prev.map(s => s.id === sectionId ? { ...s, title: newTitle } : s);
      sectionsRef.current = next;
      return next;
    });
    scheduleSectionSave();
  }, [scheduleSectionSave]);

  const handleSectionContentUpdate = useCallback((sectionId, html) => {
    sectionContentsRef.current[sectionId] = html;
    scheduleSectionSave();
  }, [scheduleSectionSave]);

  const handleAddSection = useCallback(() => {
    const newSection = {
      id: `sec-${Date.now()}`,
      title: `섹션 ${(sectionsRef.current.length || 0) + 1}`,
      sortOrder: sectionsRef.current.length,
      content: '',
    };
    setSections(prev => {
      const next = [...(prev || []), newSection];
      sectionsRef.current = next;
      return next;
    });
    sectionContentsRef.current[newSection.id] = '';
    scheduleSectionSave();
  }, [scheduleSectionSave]);

  const handleDeleteSection = useCallback((sectionId) => {
    if (!confirm('이 섹션을 삭제하시겠습니까?')) return;
    setSections(prev => {
      const next = prev.filter(s => s.id !== sectionId).map((s, i) => ({ ...s, sortOrder: i }));
      sectionsRef.current = next;
      return next;
    });
    delete sectionContentsRef.current[sectionId];
    scheduleSectionSave();
  }, [scheduleSectionSave]);

  // dnd-kit 설정
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    setSections(prev => {
      const oldIndex = prev.findIndex(s => s.id === active.id);
      const newIndex = prev.findIndex(s => s.id === over.id);
      const next = arrayMove(prev, oldIndex, newIndex).map((s, i) => ({ ...s, sortOrder: i }));
      sectionsRef.current = next;
      return next;
    });
    scheduleSectionSave();
  }, [scheduleSectionSave]);

  // 레거시 → 섹션 전환
  const convertToSections = async () => {
    const html = legacyEditorRef.current?.getHTML() || log?.content || '';
    const firstSection = { id: `sec-${Date.now()}`, title: '본문', sortOrder: 0, content: html };
    setSections([firstSection]);
    sectionContentsRef.current = { [firstSection.id]: html };
    sectionsRef.current = [firstSection];
    await api.put(`/research-logs/${id}`, { sections: [firstSection] });
    setSaveStatus('saved');
    clearTimeout(saveStatusTimer.current);
    saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
  };

  // 기타 핸들러
  const handleCategoryChange = async (catId) => {
    await api.put(`/research-logs/${id}`, { categoryId: catId || null });
    loadLog();
  };

  const handleDelete = async () => {
    if (!confirm('이 연구일지를 삭제하시겠습니까?')) return;
    await api.delete(`/research-logs/${id}`);
    navigate('/work/product/research');
  };

  const canDelete = log?.createdBy === user.id || isAdmin;
  const isSectionMode = sections !== null && sections !== undefined;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* ── 헤더 ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-950 flex-shrink-0 gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button onClick={() => navigate('/work/product/research')}
            className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800 flex-shrink-0">
            <ArrowLeft size={18} />
          </button>
          <input
            value={title}
            onChange={handleTitleChange}
            placeholder="제목을 입력하세요"
            className="flex-1 bg-transparent text-white text-lg font-bold focus:outline-none placeholder-gray-600 min-w-0"
            readOnly={viewMode}
          />
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* 카테고리 */}
          {!viewMode && (
            <select
              value={log?.categoryId || ''}
              onChange={e => handleCategoryChange(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-gray-300 text-xs focus:outline-none focus:border-brand"
            >
              <option value="">카테고리 없음</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}

          {/* 접속자 */}
          <div className="flex -space-x-1.5">
            {peers.slice(0, 5).map((p, i) => (
              <div key={i}
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${p.isMe ? 'border-brand' : 'border-gray-950'}`}
                style={{ backgroundColor: p.color, color: avatarColor(p.color) }}
                title={p.isMe ? `${p.name} (나)` : p.name}>
                {p.name?.charAt(0)}
              </div>
            ))}
          </div>

          {/* 저장 상태 */}
          {saveStatus === 'saving' && <span className="text-gray-500 text-xs">저장 중...</span>}
          {saveStatus === 'saved' && <span className="text-green-500 text-xs">저장됨</span>}
          {saveStatus === 'error' && <span className="text-red-400 text-xs">저장 실패</span>}
          {!connected && (
            <div className="flex items-center gap-1 text-red-400 text-xs"><WifiOff size={14} /> 오프라인</div>
          )}

          {/* 보기/편집 토글 */}
          <button
            onClick={() => setViewMode(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              viewMode
                ? 'bg-brand text-white hover:bg-brand/80'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {viewMode ? <><Edit2 size={13} /> 편집</> : <><Eye size={13} /> 보기</>}
          </button>

          {canDelete && (
            <button onClick={handleDelete} className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-gray-800">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* ── 메타 ─────────────────────────────────────────────────── */}
      {log && (
        <div className="px-6 py-2 text-xs text-gray-500 border-b border-gray-800/50 flex-shrink-0 flex items-center gap-3">
          <span>{log.author?.name}</span>
          <span>{dayjs(log.createdAt).format('YYYY.MM.DD HH:mm')}</span>
          {log.category && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: log.category.color }} />
              <span style={{ color: log.category.color }}>{log.category.name}</span>
            </span>
          )}
          {isSectionMode && (
            <span className="flex items-center gap-1 text-gray-600">
              <Layers size={11} /> {sections.length}개 섹션
            </span>
          )}
        </div>
      )}

      {/* ── 편집 모드: 툴바 ──────────────────────────────────────── */}
      {!viewMode && (
        <EditorToolbar
          editor={activeEditor}
          onImageUpload={handleImageButton}
          onMarkdownUpload={handleMdUpload}
        />
      )}

      {/* ── 콘텐츠 영역 ─────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {sections === undefined ? (
          // 로딩 중
          <div className="flex items-center justify-center h-32 text-gray-600 text-sm">불러오는 중...</div>
        ) : viewMode ? (
          /* ── 보기 모드 ────────────────────────────────────────── */
          isSectionMode ? (
            <div className="px-4 py-4">
              {sections.length === 0 ? (
                <div className="text-center py-12 text-gray-600 text-sm">섹션이 없습니다.</div>
              ) : (
                sections.map(sec => (
                  <AccordionSection
                    key={sec.id}
                    section={sec}
                    isOpen={!!openSections[sec.id]}
                    onToggle={() => setOpenSections(p => ({ ...p, [sec.id]: !p[sec.id] }))}
                    theme={theme}
                  />
                ))
              )}
            </div>
          ) : (
            // 레거시 보기
            <div
              className={`px-6 py-4 prose max-w-none ${theme === 'light' ? '' : 'prose-invert'}`}
              dangerouslySetInnerHTML={{ __html: log?.content || '' }}
            />
          )
        ) : (
          /* ── 편집 모드 ────────────────────────────────────────── */
          isSectionMode ? (
            <div className="px-4 py-4">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  {sections.map(sec => (
                    <SortableSection
                      key={sec.id}
                      section={sec}
                      onTitleChange={handleSectionTitleChange}
                      onDelete={handleDeleteSection}
                      ydoc={ydoc}
                      provider={provider}
                      onContentUpdate={handleSectionContentUpdate}
                      onFocus={setActiveEditor}
                      logId={log?.id}
                      onAttachRefresh={() => setAttachKey(k => k + 1)}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {/* 섹션 추가 버튼 */}
              <button
                onClick={handleAddSection}
                className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-gray-700 rounded-xl text-gray-500 hover:border-brand hover:text-brand text-sm transition-colors"
              >
                <Plus size={14} /> 섹션 추가
              </button>
            </div>
          ) : (
            // 레거시 편집 모드
            <div className="flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto">
                <EditorContent editor={legacyEditor} />
              </div>
              {/* 섹션으로 전환 */}
              <div className="px-4 py-2 border-t border-gray-800/50 flex items-center gap-2">
                <button
                  onClick={convertToSections}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-400 text-xs rounded-lg hover:bg-gray-700 hover:text-white transition-colors"
                >
                  <Layers size={12} /> 섹션 형태로 전환
                </button>
                <span className="text-gray-600 text-xs">기존 내용을 첫 번째 섹션으로 가져옵니다.</span>
              </div>
            </div>
          )
        )}
      </div>

      {/* ── 첨부파일 (편집 모드) ─────────────────────────────────── */}
      {!viewMode && (
        <FileAttachments key={attachKey} entityType="research" entityId={log?.id} editor={activeEditor} />
      )}

      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
      <input ref={mdFileInputRef} type="file" accept=".md,text/markdown" className="hidden" onChange={handleMdFile} />
    </div>
  );
}
